
/** Tier 2 — Business-facing generic emails (acceptable fallback) */
const GENERIC_PREFIXES = ['info@', 'hello@', 'contact@', 'sales@', 'accounts@', 'enquiries@', 'office@', 'mail@', 'reception@', 'general@', 'admin@'];

/** Tier 3 — Functional/system emails (never used for outreach) */
const FUNCTIONAL_PREFIXES = [
  'noreply@', 'no-reply@', 'donotreply@', 'do-not-reply@',
  'reports@', 'incidents@', 'alerts@', 'notifications@',
  'tickets@', 'helpdesk@', 'support@',
  'feedback@', 'survey@', 'bounces@', 'mailer-daemon@',
  'system@', 'automated@', 'auto@', 'daemon@', 'robot@', 'bot@',
  'webmaster@', 'postmaster@', 'abuse@',
  'unsubscribe@', 'newsletter@', 'compliance@', 'legal@',
];

const INVALID_DOMAINS = ['example.com', 'sentry.io', 'wixpress.com'];

const FREEMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
  'icloud.com', 'aol.com', 'mail.com', 'protonmail.com', 'zoho.com',
  'ymail.com', 'gmx.com', 'me.com', 'msn.com',
];

/** Check if an email is from a free email provider (gmail, yahoo, etc.) */
export const isFreemailDomain = (email: string): boolean => {
  const domain = email.split('@')[1]?.toLowerCase() || '';
  return FREEMAIL_DOMAINS.includes(domain);
};

/** Common business suffixes to strip when normalizing company names */
const COMPANY_SUFFIXES = [
  'limited', 'ltd', 'inc', 'incorporated', 'corp', 'corporation',
  'group', 'holdings', 'plc', 'llc', 'llp', 'co', 'company',
  'services', 'solutions', 'international', 'ireland', 'uk',
  'the', 'and',
];

/**
 * Normalize a company name for domain matching:
 * lowercase, strip punctuation/spaces/common suffixes
 */
const normalizeName = (name: string): string => {
  let n = name.toLowerCase();
  // Replace & with 'and' then strip it, strip all non-alpha
  n = n.replace(/&/g, '');
  n = n.replace(/[^a-z0-9]/g, '');
  // Strip common suffixes
  for (const suffix of COMPANY_SUFFIXES) {
    // Only strip if it leaves at least 3 chars
    if (n.endsWith(suffix) && n.length - suffix.length >= 3) {
      n = n.slice(0, n.length - suffix.length);
    }
  }
  return n;
};

/**
 * Extract the domain from an email address, minus TLD
 * e.g. "sales@printbureau.ie" → "printbureau"
 */
const extractDomainBase = (email: string): string => {
  const domain = email.split('@')[1]?.toLowerCase() || '';
  // Strip TLD (last dot segment)
  const parts = domain.split('.');
  if (parts.length > 1) parts.pop();
  return parts.join('');
};

/**
 * Extract domain from a website URL
 * e.g. "http://www.printbureau.ie/" → "printbureau.ie"
 */
const extractWebsiteDomain = (website: string): string => {
  try {
    let url = website.trim();
    if (!url.startsWith('http')) url = 'https://' + url;
    const hostname = new URL(url).hostname.replace(/^www\./, '');
    return hostname.toLowerCase();
  } catch {
    return '';
  }
};

/**
 * Check if an email's domain is relevant to the given company name and/or website.
 */
export const isDomainRelevant = (email: string, leadName?: string, leadWebsite?: string): boolean => {
  const emailDomain = email.split('@')[1]?.toLowerCase() || '';
  if (!emailDomain) return false;

  // 1. Exact website domain match
  if (leadWebsite) {
    const websiteDomain = extractWebsiteDomain(leadWebsite);
    if (websiteDomain && emailDomain === websiteDomain) return true;
  }

  // 2. Fuzzy name-to-domain match
  if (leadName) {
    const normalizedName = normalizeName(leadName);
    const domainBase = extractDomainBase(email);
    if (normalizedName.length >= 3 && domainBase.length >= 3) {
      // Check substring in both directions
      if (domainBase.includes(normalizedName) || normalizedName.includes(domainBase)) return true;
      // Check significant overlap (at least 60% of shorter string)
      const shorter = normalizedName.length <= domainBase.length ? normalizedName : domainBase;
      const longer = normalizedName.length > domainBase.length ? normalizedName : domainBase;
      // Sliding window match
      for (let i = 0; i <= longer.length - Math.ceil(shorter.length * 0.6); i++) {
        let matches = 0;
        for (let j = 0; j < shorter.length && i + j < longer.length; j++) {
          if (shorter[j] === longer[i + j]) matches++;
        }
        if (matches >= shorter.length * 0.6) return true;
      }
    }
  }

  return false;
};

/**
 * Detects Cloudflare-obfuscated emails and other common scraping artifacts.
 * These should never be stored or displayed.
 */
export const isInvalidEmail = (email: string): boolean => {
  const lower = email.toLowerCase().trim();
  const [localPart, domain] = lower.split('@');
  if (!localPart || !domain) return true;
  if (localPart.includes('protected')) return true;
  if (INVALID_DOMAINS.includes(domain)) return true;
  return false;
};

/** Tier 3 — system/functional emails that should never be used for outreach */
export const isFunctionalEmail = (email: string): boolean => {
  const lower = email.toLowerCase();
  return FUNCTIONAL_PREFIXES.some(p => lower.startsWith(p));
};

/** Tier 2 — business-facing generic emails (acceptable as last resort) */
export const isGenericEmail = (email: string): boolean => {
  const lower = email.toLowerCase();
  return GENERIC_PREFIXES.some(p => lower.startsWith(p));
};

export const parseLeadEmails = (lead: any): string[] => {
  if (!lead.email) return [];
  let raw: string[] = [];
  if (typeof lead.email === 'string') raw = [lead.email];
  else if (Array.isArray(lead.email)) {
    raw = lead.email.map((e: any) => (typeof e === 'string' ? e : e?.email || '')).filter(Boolean);
  } else if (typeof lead.email === 'object') {
    raw = [lead.email.email || lead.email.primary || ''].filter(Boolean);
  }
  return raw.map(e => e.toLowerCase().trim()).filter(e => !isInvalidEmail(e) && !isFunctionalEmail(e));
};

/** Sort emails so domain-relevant ones come first */
const sortByRelevance = (emails: string[], leadName?: string, leadWebsite?: string): string[] => {
  if (!leadName && !leadWebsite) return emails;
  return [...emails].sort((a, b) => {
    const aRelevant = isDomainRelevant(a, leadName, leadWebsite) ? 0 : 1;
    const bRelevant = isDomainRelevant(b, leadName, leadWebsite) ? 0 : 1;
    return aRelevant - bRelevant;
  });
};

export type SmartEmailResult = { email: string; contactName?: string; contactPosition?: string; source: string };

export const getSmartEmail = (lead: any, contacts: any[] | undefined): SmartEmailResult | null => {
  const leadEmails = parseLeadEmails(lead);
  const leadName = lead.name || '';
  const leadWebsite = lead.website || '';

  // Filter out contacts who have left the company
  const activeContacts = contacts?.filter(c => c.status !== 'left');

  // 1. Lead's own non-generic email — prefer domain-relevant
  const personalLeadEmails = leadEmails.filter(e => !isGenericEmail(e));
  const sortedPersonal = sortByRelevance(personalLeadEmails, leadName, leadWebsite);
  if (sortedPersonal.length > 0) return { email: sortedPersonal[0], source: 'lead' };

  if (activeContacts && activeContacts.length > 0) {
    // Sort contacts: domain-relevant emails first
    const sortedContacts = [...activeContacts].sort((a, b) => {
      const aRel = a.email && isDomainRelevant(a.email, leadName, leadWebsite) ? 0 : 1;
      const bRel = b.email && isDomainRelevant(b.email, leadName, leadWebsite) ? 0 : 1;
      return aRel - bRel;
    });

    // 2a. Primary with domain-relevant email
    const relevantPrimary = sortedContacts.find(c => c.is_primary && c.email && !isFunctionalEmail(c.email) && isDomainRelevant(c.email, leadName, leadWebsite));
    if (relevantPrimary) return { email: relevantPrimary.email.toLowerCase().trim(), contactName: relevantPrimary.name, contactPosition: relevantPrimary.position, source: 'key_contact_primary' };

    // 2b. Primary with freemail or no website to compare — can't prove irrelevant
    const primary = sortedContacts.find(c => c.is_primary && c.email && !isFunctionalEmail(c.email));
    if (primary && (!leadWebsite || isFreemailDomain(primary.email))) {
      return { email: primary.email.toLowerCase().trim(), contactName: primary.name, contactPosition: primary.position, source: 'key_contact_primary' };
    }
    // If primary has a non-matching business domain email, skip to step 3

    // 3. First key contact with personal (non-generic, non-functional) email
    const personalContact = sortedContacts.find(c => c.email && !isGenericEmail(c.email) && !isFunctionalEmail(c.email));
    if (personalContact) return { email: personalContact.email.toLowerCase().trim(), contactName: personalContact.name, contactPosition: personalContact.position, source: 'key_contact' };

    // 4. Any key contact with non-functional email
    const anyContact = sortedContacts.find(c => c.email && !isFunctionalEmail(c.email));
    if (anyContact) return { email: anyContact.email.toLowerCase().trim(), contactName: anyContact.name, contactPosition: anyContact.position, source: 'key_contact_generic' };

    // 4.5. Contacts exist but none have direct email — still use best contact's name for addressing
    const sortedLeadEmails = sortByRelevance(leadEmails, leadName, leadWebsite);
    const fallbackEmail = sortedLeadEmails[0] || null;
    if (fallbackEmail) {
      const bestContact = activeContacts.find(c => c.is_primary) || activeContacts[0];
      return { email: fallbackEmail, contactName: bestContact.name, contactPosition: bestContact.position, source: 'key_contact_no_email' };
    }
  }

  // 5. Fallback to generic lead email — prefer domain-relevant
  const genericLeadEmails = leadEmails.filter(e => isGenericEmail(e));
  const sortedGeneric = sortByRelevance(genericLeadEmails, leadName, leadWebsite);
  if (sortedGeneric.length > 0) return { email: sortedGeneric[0], source: 'lead_generic' };

  // 6. Any lead email at all — prefer domain-relevant
  const sortedAll = sortByRelevance(leadEmails, leadName, leadWebsite);
  if (sortedAll.length > 0) return { email: sortedAll[0], source: 'lead' };

  return null;
};
